import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import { sendAssistantMessage } from '../utils/assistant';

const position = { position: 'fixed', right: '1rem', bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))', zIndex: 1500 };
const buttonStyle = { border: '1px solid #0f766e', borderRadius: '4px', padding: '0.55rem 0.8rem', backgroundColor: '#0f766e', color: 'white', cursor: 'pointer' };

const AssistantPanel = ({ currentUser, locations }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(0);
  const historyDraftRef = useRef('');
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; clearTimeout(timerRef.current); controllerRef.current?.abort(); };
  }, []);
  useEffect(() => {
    if (open && !sending) inputRef.current?.focus();
  }, [open, sending]);
  useEffect(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, sending, open]);
  const close = () => { setOpen(false); launcherRef.current?.focus(); };
  const submit = async event => {
    event.preventDefault();
    if (controllerRef.current) return;
    const text = input.trim();
    if (!text || text.length > 2000) { setError('Enter a message between 1 and 2,000 characters.'); return; }
    const controller = new AbortController();
    controllerRef.current = controller;
    historyRef.current = [...historyRef.current, text];
    historyIndexRef.current = historyRef.current.length;
    historyDraftRef.current = '';
    timerRef.current = setTimeout(() => controller.abort(), 60000);
    setSending(true);
    setError('');
    setInput('');
    setMessages(previous => [...previous, { role: 'user', text }]);
    try {
      const result = await sendAssistantMessage((path, options) => fetch(`${API_BASE_URL}${path}`, options), text, currentUser.sessionId, controller.signal);
      if (mountedRef.current) setMessages(previous => [...previous, { role: 'assistant', text: result.response, action: result.action }]);
    } catch (failure) {
      if (mountedRef.current) {
        setError(failure.name === 'AbortError' ? 'The assistant request timed out. Please try again.' : failure.message);
        setInput(text);
      }
    } finally {
      clearTimeout(timerRef.current);
      controllerRef.current = null;
      if (mountedRef.current) setSending(false);
    }
  };
  const openScheduler = action => {
    if (action.locationId != null && !locations.some(location => Number(location.id) === action.locationId)) {
      setError('That scheduling location is not available in this organization.');
      return;
    }
    navigate('/calendar', { state: { assistantScheduler: action } });
    setOpen(false);
  };
  const handleInputKeyDown = event => {
    const textarea = event.currentTarget;
    const atStart = textarea.selectionStart === 0 && textarea.selectionEnd === 0;
    const atEnd = textarea.selectionStart === input.length && textarea.selectionEnd === input.length;
    if (event.key === 'ArrowUp' && atStart && historyRef.current.length) {
      event.preventDefault();
      if (historyIndexRef.current === historyRef.current.length) historyDraftRef.current = input;
      historyIndexRef.current = Math.max(0, historyIndexRef.current - 1);
      const previous = historyRef.current[historyIndexRef.current];
      setInput(previous);
      requestAnimationFrame(() => inputRef.current?.setSelectionRange(previous.length, previous.length));
      return;
    }
    if (event.key === 'ArrowDown' && atEnd && historyIndexRef.current < historyRef.current.length) {
      event.preventDefault();
      historyIndexRef.current += 1;
      const next = historyIndexRef.current === historyRef.current.length
        ? historyDraftRef.current
        : historyRef.current[historyIndexRef.current];
      setInput(next);
      requestAnimationFrame(() => inputRef.current?.setSelectionRange(next.length, next.length));
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
      event.preventDefault();
      if (!sending && input.trim()) submit(event);
    }
  };
  return (
    <>
      <button ref={launcherRef} type="button" aria-label="Real Air Assistant" aria-expanded={open} aria-controls="scheduling-assistant" onClick={() => setOpen(previous => !previous)} style={{ ...position, ...buttonStyle, display: open ? 'none' : undefined, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>Real Air Assistant</button>
      {open && <section id="scheduling-assistant" role="dialog" aria-label="Real Air Assistant" onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); close(); } }} style={{ ...position, width: 'min(420px, calc(100vw - 2rem))', height: 'min(580px, calc(100dvh - 140px))', display: 'flex', flexDirection: 'column', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.8rem 1rem', backgroundColor: '#0f766e', color: 'white', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Real Air Assistant</h2>
          <button type="button" aria-label="Close Real Air Assistant" title="Close assistant" onClick={close} style={{ border: 0, background: 'none', color: 'white', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer' }}>&times;</button>
        </header>
        <div ref={messagesRef} role="log" aria-label="Assistant conversation" aria-live="polite" aria-relevant="additions" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {messages.map((message, index) => <div key={index} style={{ padding: '0.65rem 0.75rem', borderRadius: '6px', backgroundColor: message.role === 'user' ? '#eff6ff' : '#f8fafc', color: '#263943', fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            <strong style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.8rem' }}>{message.role === 'user' ? 'You' : 'Real Air Assistant'}</strong>
            {message.text}
            {message.action && <div style={{ marginTop: '0.65rem' }}><button type="button" onClick={() => openScheduler(message.action)} style={buttonStyle}>Open scheduler</button></div>}
          </div>)}
          {sending && <p role="status" style={{ margin: 0, color: '#526671', fontSize: '0.9rem' }}>Real Air Assistant is responding...</p>}
        </div>
        <form onSubmit={submit} style={{ flexShrink: 0, borderTop: '1px solid #dee2e6', padding: '0.75rem 1rem' }}>
          {error && <p role="alert" style={{ color: '#b91c1c', margin: '0 0 0.5rem', fontSize: '0.85rem', overflowWrap: 'anywhere' }}>{error}</p>}
          <label htmlFor="assistant-message" style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem' }}>Message</label>
          <textarea ref={inputRef} id="assistant-message" required maxLength={2000} rows={3} disabled={sending} value={input} onChange={event => setInput(event.target.value)} onKeyDown={handleInputKeyDown} style={{ display: 'block', width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '0.5rem', font: 'inherit', fontSize: '0.9rem', resize: 'none' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
            <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{input.length}/2000</span>
            <button type="submit" disabled={sending || !input.trim()} style={{ ...buttonStyle, opacity: sending || !input.trim() ? 0.6 : 1 }}>{sending ? 'Sending...' : 'Send'}</button>
          </div>
        </form>
      </section>}
    </>
  );
};

const SchedulingAssistant = () => {
  const { currentUser, locations } = useApp();
  const route = useLocation();
  if (!currentUser?.sessionId || ['/', '/login'].includes(route.pathname)) return null;
  return <AssistantPanel key={`${currentUser.sessionId}:${currentUser.user?.orgId}:${currentUser.user?.id ?? currentUser.id}`} currentUser={currentUser} locations={locations || []} />;
};

export default SchedulingAssistant;